import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../stores/auth';
import { getCsrfToken } from './csrf';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Resolve the tenant subdomain from localStorage or the URL
function getCurrentSubdomain(): string | null {
  // 1. Already stored in localStorage (written on login or from query param)
  const stored = localStorage.getItem('currentSubdomain');
  if (stored) return stored;

  // 2. Fallback: check URL query param ?x-subdomain=xxx
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('x-subdomain');
    if (fromQuery) {
      localStorage.setItem('currentSubdomain', fromQuery);
      return fromQuery;
    }

    // 3. Fallback: extract from hostname (e.g. myschool.app.example.com)
    const match = window.location.hostname.match(/^([^.]+)\./);
    if (match && match[1] !== 'localhost' && match[1] !== 'www') {
      return match[1];
    }
  }

  return null;
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach tenant subdomain + CSRF headers
apiClient.interceptors.request.use(
  config => {
    const subdomain = getCurrentSubdomain();
    if (subdomain) {
      config.headers['x-tenant-subdomain'] = subdomain;
    }

    // Double-submit CSRF token for mutating requests, matched against the
    // CsrfGuard check on the backend (auth cookies run SameSite=None).
    if (config.method && UNSAFE_METHODS.has(config.method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor — auto-refresh on 401
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authStore = useAuthStore();
        await authStore.refreshAccessToken();
        return apiClient(originalRequest);
      } catch (refreshError) {
        const authStore = useAuthStore();
        authStore.clearAuthState();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ── Auth API ────────────────────────────────────────────────────────────────

export const authApi = {
  async login(credentials: { login: string; password: string }) {
    const response = await apiClient.post('/auth/tenant/login', credentials);
    return response.data;
  },

  async logout() {
    const response = await apiClient.post('/auth/tenant/logout');
    return response.data;
  },

  async refreshToken() {
    const response = await apiClient.post('/auth/tenant/refresh');
    return response.data;
  },
};

// ── Student API ─────────────────────────────────────────────────────────────

export const studentApi = {
  async getMyTimetable() {
    const response = await apiClient.get('/timetable');
    return response.data;
  },

  async getMyGrades() {
    const response = await apiClient.get('/exam-results');
    return response.data;
  },

  async getMyAttendance() {
    const response = await apiClient.get('/attendance');
    return response.data;
  },
};

export default apiClient;
